import React, { useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { Breadcrumb } from 'antd';
import { HomeOutlined } from '@ant-design/icons';

import { OFFICES_URL_PREFIX } from '../../common/consts';
import { getOfficesByCountyPageSheetQueries } from '../../common/helpers/i18n.utils';
import { slugifySearchQuery } from '../../common/helpers/searchQuery.utils';
import useCountyCityZone from '../../common/hooks/useCountyCityZone';
import useI18n from '../../common/hooks/useI18n';
import { IOffice } from '../../common/model/office.model';
import { I18nContextType } from '../../libs/i18n';

type Props = {
  offices: IOffice[];
  className?: string;
};

type ItemType = {
  label: string;
  href?: string;
};

const OfficesBreadcrumb: React.FC<Props> = ({ offices, className }) => {
  const router = useRouter();
  const i18n = useI18n();

  let [county, city, _] = useCountyCityZone();

  const validOfficesQueries = getOfficesByCountyPageSheetQueries();
  if (
    router.query.query &&
    !validOfficesQueries.includes(
      (router.query.query as string).replaceAll('--', '/')
    )
  ) {
    city = undefined;
  }

  const items: ItemType[] = useMemo(
    () =>
      getBreadcrumbItems(
        i18n,
        offices,
        county,
        city,
        router.query.slug as string
      ),
    [city, county, i18n, offices, router.query.slug]
  );

  const getElement = useCallback((label: string, href?: string) => {
    let span = <span>{label}</span>;
    if (!href) {
      return span;
    }
    if (href === '/') {
      span = <HomeOutlined alt={label} />;
    }
    return (
      <Link key={`breadcrumb-link-${label}`} href={href} legacyBehavior>
        <a>{span}</a>
      </Link>
    );
  }, []);

  const breadcrumbItems = useMemo(() => {
    return items.map((item, i) => (
      <Breadcrumb.Item key={`breadcrumb-item-${item.label}`}>
        {getElement(item.label, i === items.length - 1 ? undefined : item.href)}
      </Breadcrumb.Item>
    ));
  }, [getElement, items]);

  return (
    <div className={className}>
      <Breadcrumb>{breadcrumbItems}</Breadcrumb>
    </div>
  );
};

export default styled(OfficesBreadcrumb)`
  position: relative;
`;

export const getBreadcrumbItems = (
  i18n: I18nContextType,
  offices: IOffice[],
  county?: string,
  city?: string,
  slug?: string
): ItemType[] => {
  let result: ItemType[] = [
    { label: i18n.t('header.home'), href: '/' },
    {
      label: i18n.t('dynamic.officesByCountyPage.q.titleShort'),
      href: `/${OFFICES_URL_PREFIX}`,
    },
  ];
  if (county) {
    result.push({
      label: i18n.t(`dynamic.officesByCountyPage.${county}.titleShort`),
      href: `/${OFFICES_URL_PREFIX}/${county}`,
    });
    if (city) {
      result.push({
        label: i18n.t(
          `dynamic.officesByCountyPage.${county}/${city}.titleShort`
        ),
        href: `/${OFFICES_URL_PREFIX}/${county}/${city}`,
      });
    }
  }
  if (slug) {
    const office = offices.find((o) => slugifySearchQuery(o.name) === slug);
    if (office) {
      result.push({
        label: office.name,
      });
    }
  }
  return result;
};
